/**
 * Validation constants (Sprint 6).
 *
 * Vocabulary for UC1/UC2/UC3 dual-path validation — deterministic TS
 * rules and LLM rules running side-by-side with verdicts compared.
 *
 * Every string here is a protocol value crossing the DB / queue /
 * HTTP / UI boundaries. Pinning them as `as const` objects gives us
 * compile-time typos catching and a single source of truth.
 */
// -------------------------------------------------------------------------
// Use cases
// -------------------------------------------------------------------------
export const UC_ID = {
    UC1: 'uc1', // Completeness — required docs present, required fields present
    UC2: 'uc2', // Document cross-check — sanction/KFS/agreement agreement
    UC3: 'uc3', // CAM recompute — FOIR / LTV / DSCR recomputed vs stated vs caps
};
export const UC_IDS = [UC_ID.UC1, UC_ID.UC2, UC_ID.UC3];
// -------------------------------------------------------------------------
// Finding severity — re-exported from modules.ts to avoid duplication.
// modules.ts is the canonical definition used across all modules and
// scoring weights.
// -------------------------------------------------------------------------
export { SEVERITY, SEVERITIES_ORDERED as SEVERITIES } from './modules.js';
// -------------------------------------------------------------------------
// Finding status — re-exported from modules.ts. Sprint 6 workflow:
// OPEN → ACCEPTED | WAIVED | FALSE_POSITIVE, with SUPERSEDED for replaced findings.
// -------------------------------------------------------------------------
export { FINDING_STATUS } from './modules.js';
import { FINDING_STATUS } from './modules.js';
export const FINDING_TERMINAL_STATUSES = [
    FINDING_STATUS.ACCEPTED,
    FINDING_STATUS.WAIVED,
    FINDING_STATUS.FALSE_POSITIVE,
    FINDING_STATUS.SUPERSEDED,
];
// -------------------------------------------------------------------------
// Verdict source — dual-path (Q1)
// -------------------------------------------------------------------------
//
// A finding row's verdict_source describes which path(s) produced it.
// When both paths ran AND agreed, we collapse them into a single row
// with `both_agree`. When they disagreed, two rows are written, linked
// by paired_finding_id, each with its own verdict_source.
export const VERDICT_SOURCE = {
    /** Only the deterministic TS rule ran (LLM disabled for this rule). */
    DETERMINISTIC: 'deterministic',
    /** Only the LLM rule ran (deterministic not feasible). */
    LLM: 'llm',
    /** Both ran and agreed on the verdict. Highest-trust finding. */
    BOTH_AGREE: 'both_agree',
    /** Two paired rows exist for the same rule with opposite verdicts. */
    BOTH_DISAGREE: 'both_disagree',
};
// -------------------------------------------------------------------------
// Rule verdicts (what each path returns)
// -------------------------------------------------------------------------
export const RULE_VERDICT = {
    /** Rule check passed — no finding. */
    COMPLIANT: 'compliant',
    /** Rule check failed — finding emitted. */
    VIOLATED: 'violated',
    /** Rule could not run — missing inputs, doc not found, etc. */
    INCONCLUSIVE: 'inconclusive',
    /** Rule errored internally (crashed). */
    ERROR: 'error',
};
// -------------------------------------------------------------------------
// Validation job types — canonical definition lives in ./queues.ts. We
// re-export here for convenience + add the typed alias.
// -------------------------------------------------------------------------
export { VALIDATION_JOB } from './queues.js';
// -------------------------------------------------------------------------
// Validation run status
// -------------------------------------------------------------------------
export const VALIDATION_RUN_STATUS = {
    QUEUED: 'queued',
    RUNNING: 'running',
    SUCCEEDED: 'succeeded',
    FAILED: 'failed',
    PARTIAL: 'partial', // some UCs succeeded, others failed
};
// -------------------------------------------------------------------------
// Masters type keys
// -------------------------------------------------------------------------
export const VALIDATION_MASTERS_TYPE = {
    /** One record per rule. Content defines uc_id, severity, LLM prompt, etc. */
    VALIDATION_RULES: 'validation_rules',
    /** Per product+segment, list of mandatory + optional doc types. */
    DOCUMENT_CHECKLIST: 'document_checklist',
};
// -------------------------------------------------------------------------
// admin.config keys
// -------------------------------------------------------------------------
export const VALIDATION_CONFIG_KEY = {
    /** Whether to run the LLM path in addition to the deterministic path globally. */
    LLM_ENABLED: 'validation.llm_enabled',
    /** Bedrock model for LLM validation. Falls back to extraction model if unset. */
    BEDROCK_MODEL: 'validation.bedrock_model',
    /** Max tokens for a single LLM validation call. */
    LLM_MAX_TOKENS: 'validation.llm_max_tokens',
    /** Temperature for LLM validation. */
    LLM_TEMPERATURE: 'validation.llm_temperature',
    /** Tolerance for interest-rate mismatch in percentage points (0.01 = 1 bp). */
    INTEREST_RATE_TOLERANCE_PCT: 'validation.interest_rate_tolerance_pct',
    /** Tolerance for EMI mismatch in INR. */
    EMI_TOLERANCE_INR: 'validation.emi_tolerance_inr',
    /** Tolerance for tenure mismatch in months. */
    TENURE_TOLERANCE_MONTHS: 'validation.tenure_tolerance_months',
    /** Tolerance for loan-amount mismatch in INR. */
    LOAN_AMOUNT_TOLERANCE_INR: 'validation.loan_amount_tolerance_inr',
    /** Tolerance for FOIR/LTV recompute mismatch in percentage points. */
    RATIO_RECOMPUTE_TOLERANCE_PCT: 'validation.ratio_recompute_tolerance_pct',
    /** Auto-trigger validation when parsing completes (Q5). */
    AUTO_TRIGGER_ON_PARSE_COMPLETE: 'validation.auto_trigger_on_parse_complete',
    /**
     * Checklist mode: '17_point' shows only the LOS audit-checklist rules in
     * findings UI and runs only those rules. 'full' shows everything.
     * Defaults to '17_point' once Batch 3 lands (in admin config seed).
     */
    CHECKLIST_MODE: 'validation.checklist_mode',
    /**
     * Maximum days between sanction_date and disbursement_date before
     * checklist point 14 fires. Defaults to 30.
     */
    SANCTION_VALIDITY_DAYS: 'validation.sanction_validity_days',
};
// -------------------------------------------------------------------------
// Checklist mode values
// -------------------------------------------------------------------------
export const CHECKLIST_MODE = {
    /** Only the 15 LOS rule IDs (17 logical points) run and are visible. */
    SEVENTEEN_POINT: '17_point',
    /** Only the 12 CAM-vs-LOS comparison rules run and are visible (V2). */
    CAM_VS_LOS: 'cam_vs_los',
    /** All rules (existing 25 PDF rules + 17-point LOS rules) run and are visible. */
    FULL: 'full',
};
// -------------------------------------------------------------------------
// Rule catalog ids — canonical keys referenced from masters records and code
// -------------------------------------------------------------------------
//
// These are the *code-level* rule ids. Each corresponds to one deterministic
// function in `validation-core/src/rules/`. Masters records use the same
// key as their `record_key`, so masters → code lookup is trivial.
export const RULE_ID = {
    // ---- UC1 completeness ----
    UC1_MANDATORY_DOC_SANCTION: 'uc1.mandatory_doc.sanction_letter',
    UC1_MANDATORY_DOC_KFS: 'uc1.mandatory_doc.kfs',
    UC1_MANDATORY_DOC_LOAN_AGREEMENT: 'uc1.mandatory_doc.loan_agreement',
    UC1_MANDATORY_DOC_CAM: 'uc1.mandatory_doc.cam',
    UC1_MANDATORY_DOC_KYC: 'uc1.mandatory_doc.kyc',
    UC1_MANDATORY_DOC_ITR: 'uc1.mandatory_doc.itr',
    UC1_MANDATORY_DOC_BANK_STATEMENT: 'uc1.mandatory_doc.bank_statement',
    UC1_MANDATORY_DOC_VALUATION: 'uc1.mandatory_doc.valuation',
    UC1_MANDATORY_DOC_SALARY_SLIP: 'uc1.mandatory_doc.salary_slip',
    UC1_REQUIRED_FIELDS_PRESENT: 'uc1.required_fields_present',
    // ---- UC2 cross-check ----
    UC2_LOAN_AMOUNT_SANCTION_VS_KFS: 'uc2.loan_amount.sanction_vs_kfs',
    UC2_LOAN_AMOUNT_SANCTION_VS_AGREEMENT: 'uc2.loan_amount.sanction_vs_agreement',
    UC2_INTEREST_RATE_SANCTION_VS_KFS: 'uc2.interest_rate.sanction_vs_kfs',
    UC2_INTEREST_RATE_SANCTION_VS_AGREEMENT: 'uc2.interest_rate.sanction_vs_agreement',
    UC2_TENURE_SANCTION_VS_KFS: 'uc2.tenure.sanction_vs_kfs',
    UC2_TENURE_SANCTION_VS_AGREEMENT: 'uc2.tenure.sanction_vs_agreement',
    UC2_EMI_SANCTION_VS_KFS: 'uc2.emi.sanction_vs_kfs',
    UC2_EMI_SANCTION_VS_AGREEMENT: 'uc2.emi.sanction_vs_agreement',
    UC2_BORROWER_NAME_CONSISTENCY: 'uc2.borrower_name.consistency',
    // ---- UC3 CAM recompute ----
    UC3_FOIR_RECOMPUTED_VS_STATED: 'uc3.foir.recomputed_vs_stated',
    UC3_FOIR_VS_PRODUCT_CAP: 'uc3.foir.vs_product_cap',
    UC3_LTV_RECOMPUTED_VS_STATED: 'uc3.ltv.recomputed_vs_stated',
    UC3_LTV_VS_PRODUCT_CAP: 'uc3.ltv.vs_product_cap',
    UC3_DSCR_VS_THRESHOLD: 'uc3.dscr.vs_threshold',
    // ---- LOS workflow checks (Batch 1 — 17-point checklist) ----
    // Each maps to one or more rows of the auditor's Excel checklist.
    // Implementations land in Batch 3 (validation-core/src/rules/uc1-los-checks.ts).
    LOS_DOCUMENTS_REQUIRED: 'los.documents_required', // Pt 1
    LOS_API_STATUSES_VALIDATED: 'los.api_statuses_validated', // Pt 2
    LOS_DEDUPE_ALL_APPLICANTS_VALIDATED: 'los.dedupe_all_applicants_validated', // Pt 3
    LOS_CIBIL_SCORE_MEETS_THRESHOLD: 'los.cibil_score_meets_threshold', // Pt 4
    LOS_HUNTER_CLEAN_OR_DEVIATION: 'los.hunter_clean_or_deviation', // Pt 5
    LOS_BQS_NO_MATCH_OR_DEVIATION: 'los.bqs_no_match_or_deviation', // Pt 6
    LOS_RCU_STATUS_VALID: 'los.rcu_status_valid', // Pt 7
    LOS_NOVEL_CHECK_PERFORMED: 'los.novel_check_performed', // Pt 8, 9, 10
    LOS_LOAN_ELIGIBILITY_MATCHES_CAM: 'los.loan_eligibility_matches_cam', // Pt 11
    LOS_LOAN_WITHIN_COMPANY_CAP: 'los.loan_within_company_cap', // Pt 12
    LOS_ALL_DEVIATIONS_APPROVED: 'los.all_deviations_approved', // Pt 13
    LOS_DISBURSEMENT_WITHIN_VALIDITY: 'los.disbursement_within_validity', // Pt 14
    LOS_SANCTION_CONDITIONS_MATCH_CAM: 'los.sanction_conditions_match_cam', // Pt 15
    LOS_E_AGREEMENT_METADATA_COMPLETE: 'los.e_agreement_metadata_complete', // Pt 16
    LOS_KFS_ATTACHED_TO_LOAN_AGREEMENT: 'los.kfs_attached_to_loan_agreement', // Pt 17
    // ---- CAM-vs-LOS comparison checks (CAM Audit V2 — 12-point) ----
    // Each rule compares LOS data against the corresponding CAM-extracted
    // field(s). Findings are tagged as "matched" / "mismatch" / "skipped".
    // Implementations in validation-core/src/rules/cam-comparison.ts.
    CAM_DOCUMENTS_REQUIRED: 'cam.documents_required', // V2 Pt 1
    CAM_CIBIL_SCORE: 'cam.cibil_score', // V2 Pt 2
    CAM_INCOME_PROOF_PL_BL: 'cam.income_proof_pl_bl', // V2 Pt 3
    CAM_INCOME_PROOF_ML_HL: 'cam.income_proof_ml_hl', // V2 Pt 4
    CAM_NOVEL_CHECK: 'cam.novel_check', // V2 Pt 5
    CAM_LOAN_ELIGIBILITY: 'cam.loan_eligibility', // V2 Pt 6
    CAM_POLICY_LEVEL_CHECKS: 'cam.policy_level_checks', // V2 Pt 7
    CAM_DEVIATION_MATRIX: 'cam.deviation_matrix', // V2 Pt 8
    CAM_SANCTION_CONDITIONS: 'cam.sanction_conditions', // V2 Pt 9
    CAM_LEGAL_DETAIL: 'cam.legal_detail', // V2 Pt 10
    CAM_VALUATION_REPORT: 'cam.valuation_report', // V2 Pt 11
    CAM_PERSONAL_DISCUSSION: 'cam.personal_discussion', // V2 Pt 12
};
// -------------------------------------------------------------------------
// 17-point checklist categorization
// -------------------------------------------------------------------------
//
// Maps each LOS-rule ID to its row number in the auditor's Excel checklist.
// Used by the UI to render the 17-point summary panel and to filter
// findings when checklist_mode = '17_point'.
export const CHECKLIST_POINT_BY_RULE = {
    [`uc1.mandatory_doc.kyc`]: 1, // Documents Required (fallback to existing rule)
    [`los.documents_required`]: 1,
    [`los.api_statuses_validated`]: 2,
    [`los.dedupe_all_applicants_validated`]: 3,
    [`los.cibil_score_meets_threshold`]: 4,
    [`los.hunter_clean_or_deviation`]: 5,
    [`los.bqs_no_match_or_deviation`]: 6,
    [`los.rcu_status_valid`]: 7,
    [`los.novel_check_performed`]: 8, // PL/BL income proof
    // Point 9 (ML/HL income proof) reuses same rule with product-aware logic
    // Point 10 (Novel Check standalone) also folded into same rule
    [`los.loan_eligibility_matches_cam`]: 11,
    [`los.loan_within_company_cap`]: 12,
    [`los.all_deviations_approved`]: 13,
    [`los.disbursement_within_validity`]: 14,
    [`los.sanction_conditions_match_cam`]: 15,
    [`los.e_agreement_metadata_complete`]: 16,
    [`los.kfs_attached_to_loan_agreement`]: 17,
};
/** The 17 canonical rule IDs in checklist order (used by UI). */
export const CHECKLIST_RULE_IDS_IN_ORDER = [
    'los.documents_required', // 1
    'los.api_statuses_validated', // 2
    'los.dedupe_all_applicants_validated', // 3
    'los.cibil_score_meets_threshold', // 4
    'los.hunter_clean_or_deviation', // 5
    'los.bqs_no_match_or_deviation', // 6
    'los.rcu_status_valid', // 7
    'los.novel_check_performed', // 8 (also covers 9, 10)
    'los.loan_eligibility_matches_cam', // 11
    'los.loan_within_company_cap', // 12
    'los.all_deviations_approved', // 13
    'los.disbursement_within_validity', // 14
    'los.sanction_conditions_match_cam', // 15
    'los.e_agreement_metadata_complete', // 16
    'los.kfs_attached_to_loan_agreement', // 17
];
// -------------------------------------------------------------------------
// CAM Audit V2 — 12-point CAM-vs-LOS comparison checklist
// -------------------------------------------------------------------------
//
// New audit philosophy: every checklist point compares an LOS value
// against the corresponding CAM-extracted value, with configurable
// tolerances per field. When checklist_mode = 'cam_vs_los', the UI
// surfaces these 12 rules instead of the 17 LOS-only ones.
export const CAM_CHECKLIST_POINT_BY_RULE = {
    [`cam.documents_required`]: 1,
    [`cam.cibil_score`]: 2,
    [`cam.income_proof_pl_bl`]: 3,
    [`cam.income_proof_ml_hl`]: 4,
    [`cam.novel_check`]: 5,
    [`cam.loan_eligibility`]: 6,
    [`cam.policy_level_checks`]: 7,
    [`cam.deviation_matrix`]: 8,
    [`cam.sanction_conditions`]: 9,
    [`cam.legal_detail`]: 10,
    [`cam.valuation_report`]: 11,
    [`cam.personal_discussion`]: 12,
};
/** The 12 canonical CAM-vs-LOS rule IDs in checklist order (used by UI). */
export const CAM_CHECKLIST_RULE_IDS_IN_ORDER = [
    'cam.documents_required', // 1
    'cam.cibil_score', // 2
    'cam.income_proof_pl_bl', // 3
    'cam.income_proof_ml_hl', // 4
    'cam.novel_check', // 5
    'cam.loan_eligibility', // 6
    'cam.policy_level_checks', // 7
    'cam.deviation_matrix', // 8
    'cam.sanction_conditions', // 9
    'cam.legal_detail', // 10
    'cam.valuation_report', // 11
    'cam.personal_discussion', // 12
];
// -------------------------------------------------------------------------
// Which rules have an LLM path enabled (also authoritatively set in masters,
// but defaults baked here for first-deploy seeding).
// -------------------------------------------------------------------------
export const RULES_WITH_LLM_PATH_DEFAULT = [
    // UC2 cross-checks — high value, benefit from LLM's handling of OCR noise
    RULE_ID.UC2_LOAN_AMOUNT_SANCTION_VS_KFS,
    RULE_ID.UC2_LOAN_AMOUNT_SANCTION_VS_AGREEMENT,
    RULE_ID.UC2_INTEREST_RATE_SANCTION_VS_KFS,
    RULE_ID.UC2_INTEREST_RATE_SANCTION_VS_AGREEMENT,
    RULE_ID.UC2_EMI_SANCTION_VS_KFS,
    RULE_ID.UC2_EMI_SANCTION_VS_AGREEMENT,
    RULE_ID.UC2_BORROWER_NAME_CONSISTENCY,
    // UC3 reasoning checks — benefit from LLM's ability to read deviation notes
    RULE_ID.UC3_FOIR_RECOMPUTED_VS_STATED,
    RULE_ID.UC3_LTV_RECOMPUTED_VS_STATED,
    RULE_ID.UC3_FOIR_VS_PRODUCT_CAP,
    RULE_ID.UC3_LTV_VS_PRODUCT_CAP,
];
//# sourceMappingURL=validation.js.map